import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from './Modal';
import { SearchableDropdown, CellSelect } from '../forms';
import { useReferenceData, useBackendSearch } from '../../hooks';
import { shipperService, consigneeService } from '../../services';
import { Shipper, Consignee } from '../../types';

interface ChangeShipperConsigneeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (updates: {
    shipperId?: string;
    consigneeId?: string;
    consigneeName?: string;
    consigneeAddress?: string;
  }) => Promise<void>;
  carCount: number;
}

export const ChangeShipperConsigneeModal: React.FC<ChangeShipperConsigneeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  carCount,
}) => {
  // Form state
  const [shipperId, setShipperId] = useState<string>('');
  const [consigneeId, setConsigneeId] = useState<string>('');
  const [consigneeName, setConsigneeName] = useState<string>('');
  const [consigneeAddress, setConsigneeAddress] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  // Load all shippers for dropdown
  const { data: shippers } = useReferenceData({
    loadFn: () => shipperService.getAll({ limit: 1000 }),
    enabled: isOpen,
  });

  // Backend search for consignees
  const consigneeSearchFn = useCallback(async (query: string, limit: number, signal?: AbortSignal) => {
    return consigneeService.getAll({ query, limit, signal });
  }, []);

  const {
    results: consignees,
    isSearching: isConsigneeSearching,
    handleSearch: handleConsigneeSearch,
    loadInitialData: loadInitialConsignees,
  } = useBackendSearch<Consignee>({ searchFn: consigneeSearchFn });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShipperId('');
      setConsigneeId('');
      setConsigneeName('');
      setConsigneeAddress('');
      setError('');
    }
  }, [isOpen]);

  // Handle consignee selection from dropdown
  const handleConsigneeSelect = (consignee: Consignee) => {
    setConsigneeId(consignee.id);
    setConsigneeName(consignee.name);
    // Auto-fill address from consignee
    const addressParts = [
      consignee.address,
      consignee.street,
      consignee.city,
      consignee.transit,
    ].filter(Boolean);
    setConsigneeAddress(addressParts.join(', '));
    setError('');
  };

  // Handle consignee manual input (free text)
  const handleConsigneeInputChange = (value: string) => {
    // Clear consignee ID and address when user types manually
    setConsigneeId('');
    setConsigneeName(value);
    setConsigneeAddress('');
    setError('');
    // Trigger search for dropdown suggestions
    handleConsigneeSearch(value);
  };

  // Handle shipper change
  const handleShipperChange = (shipper: Shipper | null) => {
    setShipperId(shipper?.id || '');
    setError('');
  };

  // Validate and submit
  const handleSubmit = async () => {
    // At least one of shipper or consignee must be changed
    const trimmedShipperId = shipperId.trim();
    const trimmedConsigneeId = consigneeId.trim();
    const trimmedConsigneeName = consigneeName.trim();
    const trimmedConsigneeAddress = consigneeAddress.trim();

    const hasShipperChange = !!trimmedShipperId;
    const hasConsigneeChange = !!trimmedConsigneeId || !!trimmedConsigneeName;

    if (!hasShipperChange && !hasConsigneeChange) {
      setError('Please select a shipper or enter a consignee');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await onConfirm({
        shipperId: hasShipperChange ? trimmedShipperId : undefined,
        consigneeId: trimmedConsigneeId || undefined,
        consigneeName: hasConsigneeChange ? trimmedConsigneeName : undefined,
        consigneeAddress: hasConsigneeChange ? trimmedConsigneeAddress || undefined : undefined,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Change Shipper & Consignee"
      size="md"
      footer={
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Updating...' : `Update ${carCount} ${carCount === 1 ? 'Car' : 'Cars'}`}
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Update shipper and/or consignee for {carCount} selected {carCount === 1 ? 'car' : 'cars'}.
        You can change either one or both.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Shipper Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Shipper
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <CellSelect<Shipper>
              items={shippers}
              value={shipperId}
              onChange={handleShipperChange}
              getOptionValue={(item) => item.id}
              getOptionLabel={(item) => item.name}
              getSecondaryLabel={(item) => item.code}
              placeholder="Select shipper..."
              searchBySecondaryLabel
            />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Search by name or code
          </p>
        </div>

        {/* Consignee Selection / Manual Entry */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Consignee Name
          </label>
          <SearchableDropdown<Consignee>
            items={consignees}
            filterFn={() => true}
            getDisplayValue={(c) => c.name}
            getPrimaryLabel={(c) => (
              <div>
                <span className="font-medium">{c.name}</span>
                {c.code && <span className="ml-2 text-gray-500">({c.code})</span>}
              </div>
            )}
            getSecondaryLabel={(c) => (
              <div className="text-xs space-x-2">
                {c.address && <span>Address: {c.address}</span>}
                {c.shipper_code && <span>Shipper: {c.shipper_code}</span>}
              </div>
            )}
            onSelect={handleConsigneeSelect}
            onInputChange={handleConsigneeInputChange}
            onFocus={loadInitialConsignees}
            placeholder="Search or enter consignee name..."
            initialValue={consigneeName}
            emptyMessage={isConsigneeSearching ? "Searching..." : "No consignees found. You can enter a name manually."}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Search by name/code to select from existing, or type any name manually
          </p>
        </div>

        {/* Consignee Address (only shown when consignee name is entered) */}
        {consigneeName && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Consignee Address
            </label>
            <textarea
              value={consigneeAddress}
              onChange={(e) => setConsigneeAddress(e.target.value)}
              placeholder="Enter address (optional)..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ChangeShipperConsigneeModal;
